import React, { useEffect, useRef, useState, useContext, useCallback } from "react";
import axiosInstance from "../../context/axiosinstance";
import { AuthContext } from "../../context/AuthContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import Dashboardheader from "../../components/dashboardheader/DashboardHeader";
import {
  Button,
  Select,
  Card,
  Alert,
  Row,
  Col,
  Typography,
  Tabs,
  Divider,
  Breadcrumb,
  Radio,
  Input,
  Modal,
  Table,
  Spin,
} from "antd";
import {
  CameraOutlined,
  LoginOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import TodaysLog from "./TodaysLog";

const { Option } = Select;
const { Text } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;

/* ─────────────────────────────────────────
   Spin Overlay
───────────────────────────────────────── */
const CircularLoader = ({ message = "Processing..." }) => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      backgroundColor: "rgba(0,0,0,0.45)",
      backdropFilter: "blur(3px)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 99999,
    }}
  >
    <Spin size="large" styles={{ indicator: { color: "#ffffff" } }}>
      <div style={{ width: 80, height: 80 }} />
    </Spin>
    <p
      style={{
        marginTop: 12,
        color: "#ffffff",
        fontSize: 14,
        fontWeight: 500,
        letterSpacing: "0.5px",
        opacity: 0.9,
      }}
    >
      {message}
    </p>
  </div>
);

/* ─────────────────────────────────────────
   Loader message helper
───────────────────────────────────────── */
const getLoaderMessage = (stage) => {
  const map = {
    location: "Fetching Location, Please Wait!",
    camera: "Accessing camera...",
    submitting: "Submitting attendance...",
    leads: "Fetching customer leads...",
  };
  return map[stage] ?? "Processing...";
};

/* ─────────────────────────────────────────
   Haversine distance helper
───────────────────────────────────────── */
const getDistanceInMeters = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3;
  const toRad = (d) => (d * Math.PI) / 180;
  const φ1 = toRad(lat1), φ2 = toRad(lat2);
  const Δφ = toRad(lat2 - lat1);
  const Δλ = toRad(lon2 - lon1);
  const a =
    Math.sin(Δφ / 2) ** 2 +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/* ─────────────────────────────────────────
   base64 → File
───────────────────────────────────────── */
const base64ToFile = (base64String, fileName) => {
  const arr = base64String.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8 = new Uint8Array(n);
  while (n--) u8[n] = bstr.charCodeAt(n);
  return new File([u8], fileName, { type: mime });
};

/* ══════════════════════════════════════════════════
   Main component
══════════════════════════════════════════════════ */
export default function AttendanceCapture() {
  const navigate = useNavigate();

  /* ── Auth context ── */
  const { authData, backUrl, bssBackUrl } = useContext(AuthContext);

  /* ── Yesterday date range for "past" mode inputs ── */
  const [yesterdayMin, setYesterdayMin] = useState("");
  const [yesterdayMax, setYesterdayMax] = useState("");

  useEffect(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const y = yesterday.getFullYear();
    const m = String(yesterday.getMonth() + 1).padStart(2, "0");
    const d = String(yesterday.getDate()).padStart(2, "0");
    setYesterdayMin(`${y}-${m}-${d}T00:00`);
    setYesterdayMax(`${y}-${m}-${d}T23:59`);
  }, []);

  /* ── State ── */
  const [photoColor, setPhotoColor] = useState(false);
  const [workLocation, setWorkLocation] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState("");
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);

  const [showTodayModal, setShowTodayModal] = useState(false);
  const [isOnduty, setIsOnduty] = useState("");
  const [onDutyStatus, setIsOndutyStatus] = useState("");
  const [checkInData, setCheckInData] = useState(null);
  const [checkOutData, setCheckOutData] = useState(null);
  const [checkIn, setCheckIn] = useState({ check_in_location: "" });
  const [salesManagers, setSalesManagers] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isLive, setIsLive] = useState(true);
  const [isPast, setIsPast] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);

  /* ── Refs ── */
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const isSalesUser = !!authData?.employee?.sales_id;

  /* Default to "general" for non-sales users */
  useEffect(() => {
    if (!isSalesUser) setIsOnduty("general");
  }, [isSalesUser]);

  /* ── Fetch today's attendance ── */
  const fetchData = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`${backUrl}/api/hr/attendance/today`);
      const d = res.data;
      setCheckInData({
        check_in_time: d.first_check_in,
        status: d.status,
        photo: d.first_check_in_photo,
        location: d.check_in_location,
        latitude: d.check_in_latitude,
        longitude: d.check_in_longitude,
      });
      setCheckOutData({
        check_out_time: d.last_check_out,
        photo: d.last_check_out_photo,
        location: d.check_out_location,
        latitude: d.check_out_latitude,
        longitude: d.check_out_longitude,
      });
    } catch (err) {
      console.error("Error fetching attendance data:", err);
    }
  }, [backUrl]);

  /* ── Fetch work locations ── */
  const fetchWorkLocation = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`${backUrl}/api/emp/worklocation/`);
      setWorkLocation(res.data.results);
    } catch (err) {
      console.error("Error fetching work locations", err);
    }
  }, [backUrl]);

  useEffect(() => {
    fetchWorkLocation();
    fetchData();
  }, [fetchWorkLocation, fetchData]);

  /* ── Fetch customer leads when On-Duty → Customer Visit selected ── */
  useEffect(() => {
    if (isOnduty !== "Customer Visit" || !isSalesUser) return;

    const fetchLeads = async () => {
      try {
        setLoadingStage("leads");
        setLoading(true);
        const res = await axiosInstance.get(
          `${bssBackUrl}/leads/customer_leads/${authData?.employee?.sales_id}/`
        );
        setSalesManagers(res.data.results ?? res.data);
      } catch (err) {
        console.error("Error fetching leads:", err);
        toast.error("Failed to fetch customer leads");
      } finally {
        setLoading(false);
        setLoadingStage("");
      }
    };

    fetchLeads();
  }, [isOnduty, bssBackUrl, authData?.employee?.sales_id, isSalesUser]);

  /* ── Camera ── */
  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        await videoRef.current.play();
        setCameraReady(true);
        setPermissionDenied(false);
      }
    } catch (err) {
      console.error("Camera error:", err);
      setPermissionDenied(true);
      toast.error("Camera access denied or not available.");
    }
  }, []);

  useEffect(() => {
    startCamera();
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      setCameraReady(false);
    };
  }, [startCamera]);

  /* ── Geolocation ── */
  const getAreaName = async (lat, lng) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      const data = await res.json();
      return data?.display_name ?? null;
    } catch {
      return null;
    }
  };

  const getLocation = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation)
        return reject(new Error("Geolocation not supported"));

      const opts = { enableHighAccuracy: true, timeout: 10000, maximumAge: 10000 };

      // Watch for a high-accuracy fix first
      const watchId = navigator.geolocation.watchPosition(
        async ({ coords: { latitude, longitude, accuracy } }) => {
          if (accuracy <= 10) {
            navigator.geolocation.clearWatch(watchId);
            resolve({
              lat: latitude,
              lng: longitude,
              accuracy,
              name: await getAreaName(latitude, longitude),
            });
          }
        },
        (err) => { navigator.geolocation.clearWatch(watchId); reject(err); },
        opts
      );

      // Fallback: resolve with whatever we have after 35 s
      setTimeout(() => {
        navigator.geolocation.clearWatch(watchId);
        navigator.geolocation.getCurrentPosition(
          async ({ coords: { latitude, longitude, accuracy } }) =>
            resolve({
              lat: latitude,
              lng: longitude,
              accuracy,
              name: await getAreaName(latitude, longitude),
            }),
          reject,
          opts
        );
      }, 35_000);
    });

  /* ── Capture photo ── */
  const handleCapturePhoto = () => {
    if (!cameraReady) {
      toast.error("Camera not ready. Please wait.");
      return;
    }
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(videoRef.current, 0, 0, 320, 240);
    setCapturedPhoto(canvasRef.current.toDataURL("image/png"));
    setPhotoColor(true);
    toast.success("Photo captured — proceed with Check-In / Check-Out");
  };

  /* ── Retake photo ── */
  const handleRetakePhoto = () => {
    setCapturedPhoto(null);
    setPhotoColor(false);
  };

  /* ── Validate before submitting ── */
  const validateForm = () => {
    if (!cameraReady) { toast.error("Camera not ready."); return false; }
    if (!capturedPhoto) { toast.error("Please capture a photo first."); return false; }
    if (!checkIn.check_in_location) { toast.error("Please select a work location."); return false; }

    const loc = workLocation.find((l) => l.name === checkIn.check_in_location);
    if (!loc) { toast.error("Work location not found."); return false; }

    const isOnDutyMode = checkIn.check_in_location?.toLowerCase().trim() === "on-duty";

    if (isOnDutyMode && !isOnduty) {
      toast.error("Please select an On-Duty type.");
      return false;
    }
    if (isOnDutyMode && !isLive && !isPast) {
      toast.error("Please select Live or Past mode.");
      return false;
    }
    if (isPast && (!startTime || !endTime)) {
      toast.error("Please enter both start and end times.");
      return false;
    }
    if (isOnduty === "Customer Visit" && !selectedLead?.lead_no) {
      toast.error("Please search and select a customer lead.");
      return false;
    }
    return { loc };
  };

  /* ── Submit attendance ── */
  const handleAttendance = async (type) => {
    const validated = validateForm();
    if (!validated) return;
    const { loc } = validated;

    try {
      setLoadingStage("location");
      setLoading(true);

      let coords = {};
      try {
        coords = await getLocation();
      } catch {
        console.warn("Geolocation unavailable, continuing without coordinates.");
      }

      /* Distance check (skip if no coords) */
      if (coords.lat && coords.lng) {
        const distance = getDistanceInMeters(
          coords.lat, coords.lng,
          parseFloat(loc.latitude), parseFloat(loc.longitude)
        );
        const radius = parseFloat(loc.radius);
        if (distance > radius) {
          toast.error(
            `You are ${Math.round(distance)} m away. Must be within ${radius} m.`
          );
          return;
        }
      }

      setLoadingStage("submitting");

      const isOnDutyMode = checkIn.check_in_location?.toLowerCase().trim() === "on-duty";
      const file = base64ToFile(capturedPhoto, "attendance.png");
      const formData = new FormData();

      if (type === "Check-In") {
        formData.append("check_in_location", coords.name ?? "");
        formData.append("check_in_work_site", checkIn.check_in_location);
        formData.append("check_in_photo", file);
        formData.append("check_in_latitude", coords.lat ?? "");
        formData.append("check_in_longitude", coords.lng ?? "");
        formData.append("check_in_mode", isLive ? "LIVE" : "PAST");
        if (isPast) {
          formData.append("start_time", startTime);
          formData.append("end_time", endTime);
        }
        if (isOnDutyMode) {
          formData.append("onduty_type_check_in", isOnduty);
          formData.append("check_in_remarks", onDutyStatus ?? "");
        }
        if (isOnduty === "Customer Visit" && selectedLead) {
          formData.append("lead_data", JSON.stringify(selectedLead));
        }

        const res = await axiosInstance.post(
          `${backUrl}/api/hr/attendance/check-in/`,
          formData
        );
        toast.success(res.data.message ?? "Check-In successful");
        setCheckInData({ ...res.data, photo: capturedPhoto, location: coords.name });

      } else {
        formData.append("check_out_location", coords.name ?? "");
        formData.append("check_out_work_site", checkIn.check_in_location);
        formData.append("check_out_photo", file);
        formData.append("check_out_latitude", coords.lat ?? "");
        formData.append("check_out_longitude", coords.lng ?? "");
        formData.append("check_out_mode", isLive ? "LIVE" : "PAST");
        if (isPast) {
          formData.append("start_time", startTime);
          formData.append("end_time", endTime);
        }
        if (isOnDutyMode) {
          formData.append("onduty_type_check_out", isOnduty);
          formData.append("check_out_remarks", onDutyStatus ?? "");
        }
        if (isOnduty === "Customer Visit" && selectedLead) {
          formData.append("lead_data", JSON.stringify(selectedLead));
        }

        const res = await axiosInstance.post(
          `${backUrl}/api/hr/attendance/check-out/`,
          formData
        );
        toast.success(res.data.message ?? "Check-Out successful");
        setCheckOutData({ ...res.data, photo: capturedPhoto, location: coords.name });
      }
    } catch (err) {
      console.error("Attendance submit error:", err);
      const d = err.response?.data;
      toast.error(
        d?.non_field_errors?.[0] ??
        d?.detail ??
        d?.error ??
        d?.message ??
        err.message ??
        "Failed to submit attendance."
      );
    } finally {
      setLoading(false);
      setLoadingStage("");
    }
  };

  const activeStyle = {
    backgroundColor: "#0e497b",
    color: "white",
    borderColor: "#0e497b",
  };

  const isOnDutyMode = checkIn.check_in_location?.toLowerCase().trim() === "on-duty";

  /* ══════════════════════════════════════
     Render
  ══════════════════════════════════════ */
  return (
    <>
      {loading && <CircularLoader message={getLoaderMessage(loadingStage)} />}

      <Dashboardheader />
      <ToastContainer />

      {/* ── Breadcrumb ── */}
      <Breadcrumb style={{ margin: "16px 0 16px 20px", fontSize: 14, paddingLeft: 10 }}>
        <Breadcrumb.Item
          onClick={() => navigate(-1)}
          style={{ color: "#0e497b", cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
        >
          <ArrowLeftOutlined />
          <a id="bread-acr" style={{ color: "#0e497b" }}>Back</a>
        </Breadcrumb.Item>
        <Breadcrumb.Item onClick={() => setShowTodayModal(true)}>
          <a id="bread-acr" style={{ color: "#0e497b", cursor: "pointer" }}>Attendance Log</a>
        </Breadcrumb.Item>
      </Breadcrumb>

      <Row
        gutter={[16, 16]}
        style={{ padding: "20px" }}
        justify={checkInData || checkOutData ? "start" : "center"}
      >
        {/* ── Left card: camera + controls ── */}
        <Col xs={24} sm={24} md={12} lg={12}>
          <Card bordered style={{ borderRadius: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>

            {permissionDenied && (
              <Alert
                type="warning"
                showIcon
                message="Camera permission is required to mark attendance."
                style={{ marginBottom: 16 }}
              />
            )}

            {/* ── Video / Canvas ── */}
            <div style={{ position: "relative", textAlign: "center", marginBottom: 16 }}>
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                width="100%"
                height="auto"
                style={{
                  borderRadius: 12,
                  border: cameraReady ? "1px solid #0e497b" : "1px solid #ccc",
                  backgroundColor: "#f0f0f0",
                  maxWidth: 300,
                  display: capturedPhoto ? "none" : "block",
                  margin: "0 auto",
                }}
              />

              {!cameraReady && !capturedPhoto && (
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                  }}
                >
                  <CameraOutlined style={{ fontSize: 48, color: "#999" }} />
                  <p style={{ color: "#999", marginTop: 8 }}>Camera Off</p>
                </div>
              )}

              <canvas
                ref={canvasRef}
                width="320"
                height="240"
                style={{
                  display: capturedPhoto ? "block" : "none",
                  margin: "0 auto",
                  borderRadius: 12,
                  border: "2px solid #0e497b",
                  maxWidth: 300,
                  width: "100%",
                }}
              />
            </div>

            {/* ── Work location selector ── */}
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                Work Location
              </Text>
              <Select
                showSearch
                allowClear
                value={checkIn.check_in_location || undefined}
                onChange={(value) => setCheckIn({ check_in_location: value ?? "" })}
                placeholder="Select place of work"
                style={{ width: "90%", maxWidth: 300 }}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {workLocation
                  ?.filter((l) => l.name !== "Site Office")
                  .map((l) => (
                    <Option key={l.id} value={l.name}>
                      {l.name}
                    </Option>
                  ))}
              </Select>

              {/* Radius hint */}
              {checkIn.check_in_location && !isOnDutyMode && (
                <Text type="danger" style={{ display: "block", marginTop: 8 }}>
                  {(() => {
                    const found = workLocation.find(
                      (l) => l.name?.toLowerCase().trim() === checkIn.check_in_location?.toLowerCase().trim()
                    );
                    return found ? `You must be within ${found.radius} meters of this location` : null;
                  })()}
                </Text>
              )}

              {/* ── On-Duty extras ── */}
              {isOnDutyMode && (
                <>
                  {/* General / Customer toggle */}
                  <Radio.Group
                    onChange={(e) => setIsOnduty(e.target.value)}
                    value={isOnduty}
                    optionType="button"
                    buttonStyle="solid"
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "center",
                      gap: 8,
                      marginTop: 10,
                    }}
                  >
                    {(isSalesUser ? ["general", "Customer Visit"] : ["general"]).map((t) => (
                      <Radio.Button
                        key={t}
                        value={t}
                        style={{
                          backgroundColor: isOnduty === t ? "#0e497b" : "#fff",
                          color: isOnduty === t ? "#fff" : "#000",
                          borderColor: "#0e497b",
                        }}
                      >
                        {t === "general" ? "General" : "Customer Visit"}
                      </Radio.Button>
                    ))}
                  </Radio.Group>

                  {/* Live / Past toggle */}
                  <div style={{ marginTop: 12, display: "flex", justifyContent: "center", gap: 8 }}>
                    <Button
                      onClick={() => { setIsLive(true); setIsPast(false); }}
                      style={isLive ? activeStyle : {}}
                    >
                      Live
                    </Button>
                    <Button
                      onClick={() => { setIsLive(false); setIsPast(true); }}
                      style={isPast ? activeStyle : {}}
                    >
                      Past
                    </Button>
                  </div>

                  {/* Past time inputs */}
                  {isPast && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 12,
                        marginTop: 12,
                        flexWrap: "wrap",
                      }}
                    >
                      <div>
                        <label className="form-label">Start Time</label>
                        <input
                          type="datetime-local"
                          className="form-control"
                          value={startTime ?? ""}
                          min={yesterdayMin}
                          max={yesterdayMax}
                          onChange={(e) => setStartTime(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="form-label">End Time</label>
                        <input
                          type="datetime-local"
                          className="form-control"
                          value={endTime ?? ""}
                          min={yesterdayMin}
                          max={yesterdayMax}
                          onChange={(e) => setEndTime(e.target.value)}
                        />
                      </div>
                    </div>
                  )}

                  {/* Customer lead search */}
                  {isOnduty === "Customer Visit" && salesManagers?.length > 0 && (
                    <>
                      <div style={{ textAlign: "center", marginTop: 12 }}>
                        <Input
                          placeholder="Enter Lead No. or Phone"
                          style={{ maxWidth: 200 }}
                          value={selectedLead?.searchText ?? ""}
                          onChange={(e) => {
                            const val = e.target.value.toLowerCase();
                            const filtered = salesManagers.find(
                              (sm) =>
                                sm.lead_no?.toString().toLowerCase().includes(val) ||
                                sm.number?.toString().toLowerCase().includes(val)
                            );
                            setSelectedLead(
                              filtered
                                ? { ...filtered, searchText: e.target.value }
                                : { searchText: e.target.value }
                            );
                          }}
                        />
                      </div>

                      {selectedLead?.lead_no && (
                        <div style={{ marginTop: 16 }}>
                          <Table
                            size="small"
                            pagination={false}
                            bordered
                            scroll={{ x: 1200 }}
                            components={{
                              header: {
                                cell: (props) => (
                                  <th
                                    {...props}
                                    style={{ backgroundColor: "#0e497b", color: "white" }}
                                  >
                                    {props.children}
                                  </th>
                                ),
                              },
                            }}
                            columns={[
                              { title: "Lead No", dataIndex: "lead_no", key: "lead_no" },
                              { title: "Project", dataIndex: ["project", "title"], key: "title" },
                              { title: "Customer Name", dataIndex: "name", key: "name" },
                              {
                                title: "Phone", key: "number",
                                render: (_, r) =>
                                  [r.number, r.secondary_number].filter(Boolean).join(" / ") || "—",
                              },
                              {
                                title: "Email", key: "email",
                                render: (_, r) =>
                                  [r.email, r.secondary_email].filter(Boolean).join(" / ") || "—",
                              },
                              { title: "Status", dataIndex: ["status", "title"], key: "status" },
                              {
                                title: "Assigned On", dataIndex: "assigned_on", key: "assigned_on",
                                render: (d) => d ? dayjs(d).format("DD/MM/YYYY") : "—",
                              },
                            ]}
                            dataSource={[selectedLead]}
                            rowKey="lead_no"
                          />
                        </div>
                      )}
                    </>
                  )}

                  {/* Remarks */}
                  <TextArea
                    value={onDutyStatus}
                    onChange={(e) => setIsOndutyStatus(e.target.value)}
                    placeholder="Enter remarks / status"
                    rows={3}
                    style={{ width: "90%", maxWidth: 300, marginTop: 12 }}
                  />
                </>
              )}
            </div>

            <Divider />

            {/* ── Capture / Retake button ── */}
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              {!capturedPhoto ? (
                <Button
                  type="primary"
                  onClick={handleCapturePhoto}
                  disabled={!cameraReady}
                  style={{
                    backgroundColor: "#e53e3e",
                    color: "white",
                    borderRadius: 6,
                    width: "100%",
                    maxWidth: 300,
                    border: "none",
                  }}
                  icon={<CameraOutlined />}
                >
                  Capture Photo
                </Button>
              ) : (
                <Button
                  onClick={handleRetakePhoto}
                  style={{ borderRadius: 6, width: "100%", maxWidth: 300 }}
                  icon={<CameraOutlined />}
                >
                  Retake Photo
                </Button>
              )}
            </div>

            {/* ── Check-In / Check-Out buttons ── */}
            <div
              style={{
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                gap: 10,
                alignItems: "center",
              }}
            >
              <Button
                type="primary"
                loading={loading && loadingStage === "submitting"}
                disabled={!cameraReady || !capturedPhoto || loading}
                onClick={() => handleAttendance("Check-In")}
                style={{ ...activeStyle, borderRadius: 6, width: "90%", maxWidth: 300 }}
                icon={<LoginOutlined />}
              >
                Check-In
              </Button>

              {!isPast && (
                <Button
                  danger
                  loading={loading && loadingStage === "submitting"}
                  disabled={!cameraReady || !capturedPhoto || loading}
                  onClick={() => handleAttendance("Check-Out")}
                  icon={<LogoutOutlined />}
                  style={{ borderRadius: 6, width: "90%", maxWidth: 300 }}
                >
                  Check-Out
                </Button>
              )}
            </div>
          </Card>
        </Col>

        {/* ── Attendance log modal ── */}
        <Modal
          title="Attendance Log"
          open={showTodayModal}
          onCancel={() => setShowTodayModal(false)}
          width="90%"
          footer={[
            <Button key="close" onClick={() => setShowTodayModal(false)}>
              Close
            </Button>,
          ]}
        >
          <TodaysLog />
        </Modal>

        {/* ── Right card: check-in / check-out preview ── */}
        {(checkInData || checkOutData) && (
          <Col xs={24} sm={24} md={12} lg={12}>
            <Card
              bordered
              style={{ borderRadius: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}
            >
              <Tabs defaultActiveKey="1">
                {checkInData && (
                  <TabPane tab="Check-In" key="1">
                    <center>
                      <img
                        src={checkInData.photo}
                        alt="Check-In"
                        style={{
                          width: "100%",
                          maxWidth: 320,
                          borderRadius: 12,
                          marginTop: 8,
                          border: "1px solid #d9d9d9",
                        }}
                      />
                      <div style={{ marginTop: 16, color: "#555" }}>
                        <p>
                          <b>Time:</b>{" "}
                          {checkInData.check_in_time
                            ? dayjs(checkInData.check_in_time).format("DD/MM/YYYY HH:mm:ss")
                            : "—"}
                        </p>
                        {checkInData.latitude && checkInData.longitude && (
                          <p>
                            <b>Address:</b>{" "}
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${checkInData.latitude},${checkInData.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#1677ff", textDecoration: "underline" }}
                            >
                              {checkInData.location || "View on Google Maps"}
                            </a>
                          </p>
                        )}
                      </div>
                    </center>
                  </TabPane>
                )}

                {checkOutData && (
                  <TabPane tab="Check-Out" key="2">
                    {checkOutData.photo ? (
                      <center>
                        <img
                          src={checkOutData.photo}
                          alt="Check-Out"
                          style={{
                            width: "100%",
                            maxWidth: 320,
                            borderRadius: 12,
                            marginTop: 8,
                            border: "1px solid #d9d9d9",
                          }}
                        />
                        <div style={{ marginTop: 16, color: "#555" }}>
                          <p>
                            <b>Time:</b>{" "}
                            {checkOutData.check_out_time
                              ? dayjs(checkOutData.check_out_time).format("DD/MM/YYYY HH:mm:ss")
                              : "—"}
                          </p>
                          {checkOutData.latitude && checkOutData.longitude && (
                            <p>
                              <b>Address:</b>{" "}
                              <a
                                href={`https://www.google.com/maps/search/?api=1&query=${checkOutData.latitude},${checkOutData.longitude}`}
                                rel="noopener noreferrer"
                                style={{ color: "#1677ff", textDecoration: "underline" }}
                              >
                                {checkOutData.location || "View on Google Maps"}
                              </a>
                            </p>
                          )}
                        </div>
                      </center>
                    ) : (
                      <center style={{ padding: 24, color: "#999" }}>
                        No check-out data yet.
                      </center>
                    )}
                  </TabPane>
                )}
              </Tabs>
            </Card>
          </Col>
        )}
      </Row>
    </>
  );
}